import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ShipperCreditProfile {
  company_id: string;
  legal_name: string | null;
  dba: string | null;
  ein: string | null;
  business_structure: string | null;
  state_of_incorporation: string | null;
  years_in_business: number | null;
  duns_number: string | null;
  signer_name: string | null;
  signer_title: string | null;
  signer_email: string | null;
  signer_phone: string | null;
  physical_address_line1: string | null;
  physical_address_line2: string | null;
  physical_city: string | null;
  physical_state: string | null;
  physical_postal_code: string | null;
  physical_country: string | null;
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_postal_code: string | null;
  billing_country: string | null;
  ap_contact_name: string | null;
  ap_contact_email: string | null;
  ap_contact_phone: string | null;
  annual_freight_spend_usd: number | null;
  avg_loads_per_month: number | null;
  payment_terms_days: number | null;
  preferred_payment_method: string | null;
  factoring_friendly: boolean | null;
  bank_name: string | null;
  bank_contact_name: string | null;
  bank_contact_phone: string | null;
  bank_contact_email: string | null;
  bank_account_last4: string | null;
  bank_routing_last4: string | null;
  trade_ref_1_company: string | null;
  trade_ref_1_contact: string | null;
  trade_ref_1_phone: string | null;
  trade_ref_1_email: string | null;
  trade_ref_2_company: string | null;
  trade_ref_2_contact: string | null;
  trade_ref_2_phone: string | null;
  trade_ref_2_email: string | null;
  trade_ref_3_company: string | null;
  trade_ref_3_contact: string | null;
  trade_ref_3_phone: string | null;
  trade_ref_3_email: string | null;
  notes_for_brokers: string | null;
  // Phase 1 additions
  credit_limit_request_usd: number | null;
  sales_rep_name: string | null;
  company_phone: string | null;
  company_email: string | null;
  company_website: string | null;
  company_fax: string | null;
  business_started_on: string | null;
  industry: string | null;
  commodities_shipped: string | null;
  billing_special_requirements: string | null;
  requires_po_number: boolean | null;
  requires_invoice_docs: boolean | null;
  invoice_docs_notes: string | null;
  billing_same_as_physical: boolean | null;
  trade_ref_1_account: string | null;
  trade_ref_2_account: string | null;
  trade_ref_3_account: string | null;
  insurance_carrier: string | null;
  insurance_policy_number: string | null;
  insurance_liability_limit_usd: number | null;
  insurance_cargo_limit_usd: number | null;
  insurance_expires_on: string | null;
  // Phase 3 — typed e-signature
  signer_signature_text: string | null;
  signer_signature_saved_at: string | null;
  signer_signature_ip: string | null;
  signer_signature_user_agent: string | null;
  is_demo: boolean;
  created_at?: string;
  updated_at?: string;
}

// Required fields — mirrors the asterisked (*) fields in CompleteCreditProfileModal SECTIONS.
// Optional sections/fields do NOT gate completion.
const COMPLETION_FIELDS: (keyof ShipperCreditProfile)[] = [
  // identity
  "legal_name", "ein",
  // physical address
  "physical_address_line1", "physical_city", "physical_state", "physical_postal_code",
  // contact
  "company_phone", "company_email",
  // business info
  "business_structure", "business_started_on", "industry",
  // AP contact
  "ap_contact_name", "ap_contact_email", "ap_contact_phone",
  // authorized signer (incl. typed signature)
  "signer_name", "signer_title", "signer_email", "signer_phone", "signer_signature_text",
];

export function getCreditProfileCompletion(p: Partial<ShipperCreditProfile> | null) {
  if (!p) return { complete: 0, total: COMPLETION_FIELDS.length, percent: 0, isComplete: false };
  let complete = 0;
  for (const f of COMPLETION_FIELDS) {
    const v = p[f];
    if (v !== null && v !== undefined && v !== "" && !(typeof v === "number" && isNaN(v))) {
      complete++;
    }
  }
  const total = COMPLETION_FIELDS.length;
  return {
    complete,
    total,
    percent: Math.round((complete / total) * 100),
    isComplete: complete === total,
  };
}

export function useShipperCreditProfile(companyId: string | null | undefined) {
  const [profile, setProfile] = useState<ShipperCreditProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setProfile(null);
      setLoaded(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("shipper_credit_profiles")
      .select("*")
      .eq("company_id", companyId)
      .maybeSingle();
    if (error) setError(error.message);
    setProfile((data as ShipperCreditProfile) ?? null);
    setLoading(false);
    setLoaded(true);
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = useCallback(
    async (patch: Partial<ShipperCreditProfile>) => {
      if (!companyId) return { error: "No company" };
      const payload = { ...patch, company_id: companyId };
      const { data, error } = await supabase
        .from("shipper_credit_profiles")
        .upsert(payload, { onConflict: "company_id" })
        .select()
        .maybeSingle();
      if (!error && data) setProfile(data as ShipperCreditProfile);
      return { data, error };
    },
    [companyId]
  );

  const completion = getCreditProfileCompletion(profile);

  return { profile, loading, loaded, error, refresh: load, upsert, completion };
}
